<template>
  <q-page padding>
    <div class="row col-12 align">
      <PageHeader title="Pedidos em Aberto" />
      <p class="text-brand-5 q-ml-sm">pedidos de compra ainda não recebidos</p>
    </div>

    <section
      class="row col-12 q-mt-md q-gutter-sm text-brand-5 text-weight-bold"
    >
      <div class="col-md-2 col-2">
        <div>Unidade</div>
        <ListaUnidadesSelect :todos="true" />
      </div>

      <div class="col-md-2 col-2">
        <div>Aprovador</div>
        <q-input
          outlined
          dense
          v-model="fornecedor"
          placeholder="Prox Aprovador"
        >
        </q-input>
      </div>

      <div class="col-md-2 col-2">
        <div>Num. Pedido</div>
        <q-input
          outlined
          dense
          v-model="numero"
          placeholder="Num. Pedido"
          onkeypress="return event.charCode >= 48 && event.charCode <= 57"
        >
        </q-input>
      </div>

      <div class="col-md-2 col-2">
        <div>Status Pedido</div>
        <q-select
          :options="status_pedido"
          label="Status Pedido"
          dense
          outlined
          v-model="status"
          :rules="[(val) => val || 'Campo Obrigatório']"
        />
      </div>

      <div class="col-md-2 col-2">
        <div>Status Aprovação</div>
        <q-select
          :options="status_aprovacao"
          label="Status Aprovação"
          dense
          outlined
          v-model="aprovacao"
          :rules="[(val) => val || 'Campo Obrigatório']"
        />
      </div>

      <div class="col-md-1 col-12 self-center">
        <q-btn
          @click="getDados()"
          icon="filter_alt"
          no-caps
          style="background-color: #7ab9e0; border-radius: 8px; color: white"
          label="Filtrar"
        />
      </div>
    </section>

    <div class="q-mt-lg">
      <q-table
        flat
        table-header-class="bg-accent text-width-bold"
        title="Itens a serem recebidos"
        :rows="rows"
        title-class="text-brand-5 text-weight-bold"
        :columns="columns"
        :loading="disable"
        class="tabela-tamanho-fixo"
        dense
        hide-pagination
        v-model:pagination="pagination"
        :rows-per-page-options="[0]"
        @request="handleRequest"
      >
        <template v-slot:top-right>
          <div class="row">
            <q-btn
              class="q-ml-sm"
              color="secondary"
              icon="file_upload"
              label="Exportar"
              no-caps
              @click="exportar"
            />
          </div>
        </template>

        <template #body-cell-numero_pedido="props">
          <q-td>
            <div class="row">
              <span
                class="text-blue cursor"
                @click="rota(props.row.numero_pedido)"
              >
                {{ props.row.numero_pedido }}
              </span>
            </div>
          </q-td>
        </template>

        <template #body-cell-solicitante_nome="props">
          <q-td>
            <div class="row">
              <span>
                {{ props.row.solicitante_nome }}
              </span>
              <span> ( {{ props.row.solicitante_matricula }}) </span>
            </div>
          </q-td>
        </template>
        <template #body-cell-cost_center_code="props">
          <q-td>
            <div class="row no-wrap">
              <span>
                {{ props.row.cost_center_code }}
              </span>
              <span class="q-ml-sm"> ({{ props.row.cost_center_name }}) </span>
            </div>
          </q-td>
        </template>
        <template #body-cell-descricao_produto="props">
          <q-td>
            <span>{{ props.row.descricao_produto }}</span>
          </q-td>
        </template>
        <template #body-cell-fornecedor_nome="props">
          <q-td>
            <span>{{ props.row.fornecedor_nome }}</span>
          </q-td>
        </template>
        <template #body-cell-receber="props">
          <q-td>
            <q-btn v-if="props.row" round color="primary" icon="check" />
          </q-td>
        </template>

        <template #body-cell-editar="props">
          <q-td>
            <q-btn
              v-if="props.row"
              class="q-ml-sm"
              icon="add"
              round
              color="info"
            />
          </q-td>
        </template>
      </q-table>
      <div class="row justify-start q-mt-md">
        <q-pagination
          :disable="disable"
          v-model="pagination.page"
          color="brand-5"
          active-color="secondary"
          active-text-color="white"
          :max="pagesNumber"
          :max-pages="5"
          :boundary-numbers="false"
          :direction-links="true"
          size="sm"
        />
      </div>
    </div>
  </q-page>
</template>

<script>
const getDecimalPlaces = (num) => {
  const str = num.toFixed(10).replace(/0+$/, "");
  const dotIndex = str.indexOf(".");
  if (dotIndex === -1) return 2;
  const decimals = str.slice(dotIndex + 1);
  return Math.min(Math.max(decimals.length, 2), 4);
};

const parseValor = (value) => {
  if (value === null || value === undefined || value === "") return NaN;
  if (typeof value === "number") return value;
  const str = String(value);
  const normalizado = str.replace(/\./g, "").replace(",", ".");
  return Number(normalizado);
};

const formatNumero = (num) => {
  if (isNaN(num) || num === null || num === undefined) return "0,00";
  const decimalPlaces = getDecimalPlaces(num);
  return new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  }).format(num);
};

const formatMoeda = (num) => {
  if (isNaN(num) || num === null || num === undefined) return "R$ 0,00";
  const decimalPlaces = getDecimalPlaces(num);
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  }).format(num);
};

const formatQuantidade = (value) => formatNumero(parseValor(value));
const formatPreco = (value) => formatMoeda(parseValor(value));

const columns = [
  {
    name: "numero_pedido",
    label: "#Pedido",
    field: "numero_pedido",
    align: "left",
    style: "color: #707070; font-weight: bold",
  },

  {
    name: "produto_codigo",
    label: "Cód Produto	",
    field: "produto_codigo",
    align: "left",
    style: "color: #707070; font-weight: bold",
  },
  {
    name: "descricao_produto",
    label: "Produto",
    field: "descricao_produto",
    align: "left",
    style: "color: #707070; font-weight: bold",
  },
  {
    name: "unidade",
    label: "Un Medida",
    field: (row) => (row.unidade_medida ? row.unidade_medida : "Sem Medida"),
    align: "left",
    style: "color: #707070; font-weight: bold",
  },
  {
    name: "quantidade_em_aberto",
    label: "Qtd Aberto",
    field: (row) => formatQuantidade(row.quantidade_em_aberto),
    align: "left",
    style: "color: #707070; font-weight: bold",
  },
  {
    name: "preco_pedido",
    label: "Vlr Unitário",
    field: (row) => {
      if (!row.preco_pedido) return "Sem Valor";
      return formatPreco(row.preco_pedido);
    },
    align: "left",
    style: "color: #707070; font-weight: bold",
  },
  {
    name: "total",
    label: "Vlr Total",
    field: (row) => {
      if (!row.preco_pedido) return "Sem Valor";
      const preco = parseValor(row.preco_pedido);
      const quantidade = parseValor(row.quantidade_em_aberto ?? 0);
      return !isNaN(preco) && !isNaN(quantidade)
        ? formatPreco(preco * quantidade)
        : "R$ 0,00";
    },
    align: "left",
    style: "color: #707070; font-weight: bold",
  },
  {
    name: "fornecedor_nome",
    label: "Fornecedor",
    field: "fornecedor_nome",
    align: "left",
    style: "color: #707070; font-weight: bold",
  },
  {
    name: "fornecedor_cnpj",
    label: "CNPJ Fornecedor",
    field: "fornecedor_cnpj",
    align: "left",
    style: "color: #707070; font-weight: bold",
  },
  {
    name: "status",
    label: "Status Pedido",
    field: (row) => row.status.toUpperCase(),
    align: "left",
    style: "color: #707070; font-weight: bold",
  },
  {
    name: "status_aprovacao",
    label: "Status Aprovação",
    field: (row) => row.status_aprovacao.toUpperCase(),
    align: "left",
    style: "color: #707070; font-weight: bold",
  },
  {
    name: "proximo_aprovador_nome",
    label: "Próx Aprovador",
    field: (row) =>
      row.proximo_aprovador_nome ? row.proximo_aprovador_nome : "Sem Aprovador",
    align: "left",
    style: "color: #707070; font-weight: bold",
  },
];

import { ref, onMounted, watch, computed } from "vue";
import { useStore } from "vuex";
import { useQuasar } from "quasar";
import { useRouter } from "vue-router";
import ListaUnidadesSelect from "../../../organizacional/unidade/component/ListaUnidadesSelect.vue";
import PageHeader from "../../../../components/PageHeader.vue";

export default {
  components: {
    ListaUnidadesSelect,
    PageHeader,
  },

  setup() {
    const store = useStore();
    const $q = useQuasar();
    const rows = ref([]);
    const disable = ref(true);
    const status = ref("Todos");
    const aprovacao = ref("Todos");
    const numero = ref("");
    const router = useRouter();
    const fornecedor = ref("");
    const pagination = ref({
      sortBy: "desc",
      descending: false,
      page: 1,
      rowsPerPage: 10,
      rowsNumber: 15,
    });

    const getDados = async (page = 1, rowsPerPage = 10) => {
      store.dispatch("pdt/hiddenHeader", true);
      try {
        const response = await store.dispatch("compras/pedidosEmAberto", {
          vendor_id: store.state.org.unidadeData.id,
          page,
          per_page: rowsPerPage,
          status_aprovacao: aprovacao.value.value,
          status: status.value.value,
          nome_aprovador: fornecedor.value,
          numero_pedido: numero.value,
        });

        rows.value = response.data.data;
        pagination.value.rowsPerPage = response.data.per_page;
        pagination.value.rowsNumber = response.data.total;
        pagination.value.page = response.data.current_page;
        disable.value = false;
      } catch (error) {
        $q.notify({
          message: "Erro ao encontrar dados",
          color: "negative",
          actions: [
            {
              label: "Fechar",
              color: "white",
              handler: () => {
                /* ... */
              },
            },
          ],
        });
      }
    };

    onMounted(getDados);

    const exportar = async () => {
      try {
        await store.dispatch("compras/exportarPedidosEmAberto", {
          vendor_id: store.state.org.unidadeData.id,
          status_aprovacao: aprovacao.value.value,
          status: status.value.value,
          nome_aprovador: fornecedor.value,
          numero_pedido: numero.value,
        });

        $q.notify({
          message: "Exportado com sucesso",
          color: "positive",
          actions: [
            {
              label: "Fechar",
              color: "white",
              handler: () => {
                /* ... */
              },
            },
          ],
        });
      } catch (error) {
        $q.notify({
          message: "Erro ao exportar dados",
          color: "negative",
          actions: [
            {
              label: "Fechar",
              color: "white",
              handler: () => {
                /* ... */
              },
            },
          ],
        });
      }
    };

    const rota = (id) => {
      const url = router.resolve({
        name: "compras-editar",
        params: { id },
      }).href;
      window.open(url, "_blank");
    };

    const handleRequest = async () => {
      getDados(pagination.value.page, pagination.value.rowsPerPage);
    };

    watch(
      () => pagination.value.page,
      () => {
        disable.value = true;
        handleRequest();
      }
    );

    watch(
      () => aprovacao.value,
      () => {
        disable.value = true;
        handleRequest();
      }
    );
    watch(
      () => status.value,
      () => {
        disable.value = true;
        handleRequest();
      }
    );

    watch(
      () => numero.value,
      () => {
        getDados();
      }
    );

    watch(
      () => fornecedor.value,
      () => {
        getDados();
      }
    );

    watch(
      () => store.state.org.unidadeData,
      () => {
        getDados();
      }
    );

    return {
      pagination,
      rows,
      disable,
      columns,
      getDados,
      handleRequest,
      pagesNumber: computed(() =>
        Math.ceil(pagination.value.rowsNumber / pagination.value.rowsPerPage)
      ),

      aprovacao,

      status_pedido: [
        {
          label: "Todos",
          value: "",
        },
        {
          label: "Criado",
          value: "Criado",
        },
        {
          label: "Enviado",
          value: "Enviado",
        },
        {
          label: "Recebido Parcialmente",
          value: "recebido_parcialmente",
        },
        {
          label: "Recebido Totalmente",
          value: "recebido",
        },
        {
          label: "Atrasado",
          value: "atrasado",
        },
      ],
      status_aprovacao: [
        {
          label: "Todos",
          value: "",
        },
        {
          label: "Aprovado",
          value: "aprovado",
        },
        {
          label: "Não Iniciado",
          value: "pendente",
        },
        {
          label: "Aguardando",
          value: "aguardando",
        },
        {
          label: "Reprovado",
          value: "reprovado",
        },
      ],

      status,
      numero,
      rota,
      fornecedor,
      exportar,
    };
  },
};
</script>

<style scoped>
.align {
  align-items: center;
}

.cursor {
  cursor: pointer;
}

.no-wrap {
  flex-wrap: nowrap !important;
}
tbody tr td {
  white-space: pre-wrap;
}
</style>
